import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Mail, Home, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import LeadCaptureModal from '@/components/LeadCaptureModal';
import BackButton from '@/components/BackButton';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get proper image URL
const getImageUrl = (imagePath) => {
  const fallback = 'https://images.unsplash.com/photo-1645510807290-cc82de2749f2?crop=entropy&cs=srgb&fm=jpg&q=85';
  if (!imagePath) return fallback;
  // If it's already a full URL, check if it's from current domain or return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    // If it's from our backend domain, return as is
    if (imagePath.includes(BACKEND_URL?.replace('https://', '').replace('http://', ''))) {
      return imagePath;
    }
    // For external URLs, return as is (may or may not work)
    return imagePath;
  }
  // If it's a relative path, prepend the backend URL
  return `${BACKEND_URL}${imagePath}`;
};

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalInterest, setModalInterest] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const statusCategories = ['All', 'Upcoming', 'Under Construction', 'Completed', 'Affordable Housing'];

  // Apply filters
  let filteredProjects = projects;

  // Apply status filter
  if (statusFilter !== 'All') {
    filteredProjects = filteredProjects.filter((p) => p.status === statusFilter);
  }

  // Apply featured filter
  if (showFeaturedOnly) {
    filteredProjects = filteredProjects.filter((p) => p.featured === true);
  }

  // Filter by search query
  if (searchQuery) {
    filteredProjects = filteredProjects.filter((p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.address.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  const handleBookVisit = (project, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedProject(project);
    setModalTitle(`Book a Site Visit - ${project.name}`);
    setModalInterest('Tenant');
    setIsModalOpen(true);
  };

  const handleEnquire = (project, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedProject(project);
    setModalTitle(`Enquire Now - ${project.name}`);
    setModalInterest('Investor');
    setIsModalOpen(true);
  };

  return (
    <div className="portfolio-page">
      {/* Hero Section with Background Image */}
      <section 
        className="relative py-32 pt-40 text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1590560041498-ef79363d9f0c?w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/60 to-black/70"></div>
        
        {/* Back Button */}
        <div className="absolute top-24 left-6 z-10">
          <BackButton />
        </div>

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 animate-in">Property Showcase</h1>
          <p className="text-lg sm:text-xl max-w-3xl text-gray-200 animate-in" style={{ animationDelay: '0.2s' }}>
            Explore our curated mix of residential and mixed-use projects built with vision, quality, and community in mind.
          </p>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-6">
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by property name or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 text-base rounded-full border-2 focus:border-[#20B2AA] shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Filter Section */}
          <div className="space-y-4">
            {/* Status Filters */}
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2 font-medium">Filter by Status:</p>
              <div className="flex flex-wrap gap-3 justify-center">
                {statusCategories.map((category) => (
                  <Button
                    key={category}
                    data-testid={`filter-${category.toLowerCase().replace(/ /g, '-')}`}
                    onClick={() => setStatusFilter(category)}
                    variant={statusFilter === category ? 'default' : 'outline'}
                    className={`rounded-full px-6 py-2 transition-all duration-200 ${
                      statusFilter === category
                        ? 'bg-[#20B2AA] hover:bg-[#189a93] text-white'
                        : 'border-gray-300 text-gray-700 hover:border-[#20B2AA] hover:text-[#20B2AA]'
                    }`}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>

            {/* Featured Filter */}
            <div className="text-center">
              <Button
                data-testid="filter-featured"
                onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
                variant={showFeaturedOnly ? 'default' : 'outline'}
                className={`rounded-full px-8 py-2 transition-all duration-200 ${
                  showFeaturedOnly
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    : 'border-gray-300 text-gray-700 hover:border-yellow-500 hover:text-yellow-600'
                }`}
              >
                ⭐ Featured
              </Button>
            </div>
          </div>

          {/* Navigate to Units Button */}
          <div className="flex justify-center mt-8">
            <Link to="/units">
              <Button 
                className="bg-gradient-to-r from-[#20B2AA] to-[#189a93] hover:from-[#189a93] hover:to-[#157d77] text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 flex items-center gap-3"
              >
                <Home className="h-6 w-6" />
                Explore Units for Sale/Lease
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>

          {/* Results Count */}
          {searchQuery && (
            <div className="text-center text-sm text-gray-600">
              Found {filteredProjects.length} {filteredProjects.length === 1 ? 'property' : 'properties'}
            </div>
          )}
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No projects found in this category.</p>
              {statusFilter !== 'All' && (
                <p className="text-gray-400 text-sm mt-2">
                  Try selecting "All" or check if properties have the "{statusFilter}" status assigned in the admin panel.
                </p>
              )}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <Link to={`/portfolio/${project.slug || project.id}`} key={project.id}>
                  <div
                    data-testid={`project-card-${index}`}
                    className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer scale-in relative"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-gray-100 relative">
                      <img
                        src={getImageUrl(project.image_url)}
                        alt={project.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      
                      {/* Status Badge */}
                      {project.status && (
                        <div className="absolute top-4 left-4 px-3 py-1.5 bg-[#20B2AA] text-white text-xs font-semibold rounded-full shadow-lg">
                          {project.status}
                        </div>
                      )}
                      
                      {/* Featured Badge */}
                      {project.featured && (
                        <div className="absolute top-4 right-4 px-3 py-1.5 bg-yellow-500 text-white text-xs font-semibold rounded-full shadow-lg">
                          ⭐ Featured
                        </div>
                      )}
                      
                      {/* Hover Overlay with Buttons */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6 px-4 gap-3">
                        <Button
                          onClick={(e) => handleBookVisit(project, e)}
                          className="bg-white text-[#20B2AA] hover:bg-[#20B2AA] hover:text-white px-6 py-2 rounded-full font-semibold transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 delay-100"
                        >
                          <Calendar className="h-4 w-4 mr-2" />
                          Book a Site Visit
                        </Button>
                        <Button
                          onClick={(e) => handleEnquire(project, e)}
                          className="bg-[#20B2AA] text-white hover:bg-[#189a93] px-6 py-2 rounded-full font-semibold transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 delay-150"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Enquire Now
                        </Button>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="inline-block px-3 py-1 bg-[#20B2AA]/10 text-[#20B2AA] text-xs font-medium rounded-full mb-3">
                        {project.category}
                      </div>
                      <h3 className="text-2xl font-bold mb-2 text-black group-hover:text-[#20B2AA] transition-colors duration-200 whitespace-pre-line">
                        {project.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3">{project.address}</p>
                      <div className="flex gap-4 text-sm text-gray-500 mb-3">
                        {project.units && <span>{project.units} Units</span>}
                        {project.year && <span>• {project.year}</span>}
                      </div>
                      {project.amenities && project.amenities.length > 0 && (
                        <div className="border-t border-gray-200 pt-3 mt-3">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Key Amenities</p>
                          <div className="flex flex-wrap gap-2">
                            {project.amenities.slice(0, 3).map((amenity, idx) => (
                              <span key={idx} className="text-xs text-gray-600 flex items-center gap-1">
                                <svg className="w-4 h-4 text-[#20B2AA]" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                                {amenity}
                              </span>
                            ))}
                            {project.amenities.length > 3 && (
                              <span className="text-xs text-gray-500">+{project.amenities.length - 3} more</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProject(null);
        }}
        title={modalTitle}
        defaultInterest={modalInterest}
        source="general"
        formLocation="portfolio"
        propertyInterest={selectedProject?.name}
      />
    </div>
  );
};

export default Portfolio;
